import axios from "axios";

function cleanGoogleDescription(html) {
    if (!html) return "No description available.";
    
    // 1. Replace closing paragraph and break tags with newlines
    let text = html.replace(/<\/p>/gi, '\n').replace(/<br\s*\/?>/gi, '\n');
    
    // 2. Strip all other HTML tags (like <b>, <i>, <p>, <ul>)
    text = text.replace(/<[^>]+>/g, '');
    
    // 3. Decode common HTML entities (optional but good for &amp; etc)
    text = text.replace(/&nbsp;/g, ' ')
               .replace(/&amp;/g, '&')
               .replace(/&lt;/g, '<')
               .replace(/&gt;/g, '>');
               
    return text.trim();
}

export async function getGoogleCalendarEvents() {
    const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
    const CALENDAR_ID = process.env.REACT_APP_GOOGLE_CALENDAR_ID;

    if (!API_KEY || !CALENDAR_ID) {
        console.warn("Google API Key or Calendar ID is missing in .env. Skipping Google Calendar API calls.");
        return []; // Return empty array if configuration is missing
    }

    const GOOGLE_URL = `https://www.googleapis.com/calendar/v3/calendars/${CALENDAR_ID}/events`;

    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30); // TODO: decide how many announcements to display. for now, it is within 30 days of the call

    try {
        const response = await axios.get(GOOGLE_URL, {
            params: {
                key: API_KEY,
                timeMin: new Date().toISOString(), // Fetch events starting from now
                timeMax: maxDate.toISOString(), // Stop fetching after this date
                singleEvents: true,               // Expand recurring events
                orderBy: 'startTime',             // Order by start time
                maxResults: 100                    // Limit the number of events to fetch
            }
        });

        //console.log("Fetched Google Calendar events:", response.data);

        // Transform Google event data into your Announcement format
        const transformedEvents = response.data.items.map((event) => {
            let displayTime = "Unknown Date";

            // --- DATE FORMATTING LOGIC ---
            if (event.start.dateTime) {
                // SCENARIO 1: Timed Event (e.g., 10am - 11am)
                const start = new Date(event.start.dateTime);
                const end = new Date(event.end.dateTime);

                // Format options: "Jan 12, 2026"
                const dateOptions = { day: 'numeric', month: 'short', year: 'numeric' };
                // Format options: "10:00 AM" (No seconds!)
                const timeOptions = { hour: '2-digit', minute: '2-digit' };

                const dateStr = start.toLocaleDateString('en-SG', dateOptions);
                const startTime = start.toLocaleTimeString('en-SG', timeOptions);
                const endTime = end.toLocaleTimeString('en-SG', timeOptions);

                // Check if it ends on the same day
                if (start.toDateString() === end.toDateString()) {
                    // Result: "Jan 12, 2026 • 10:00 AM - 11:30 AM"
                    displayTime = `${dateStr} • ${startTime} - ${endTime}`;
                } else {
                    // Result: "Jan 12, 8:00 PM - Jan 13, 10:00 AM"
                    const endDateStr = end.toLocaleDateString('en-SG', { day: 'numeric', month: 'short' });
                    displayTime = `${dateStr}, ${startTime} - ${endDateStr}, ${endTime}`;
                }

            } else if (event.start.date) {
                // SCENARIO 2: All-Day Event
                const start = new Date(event.start.date);
                // Google "end" date for all-day events is exclusive (the day after), so usually just showing Start Date is cleaner
                // Result: "Jan 12, 2026"
                displayTime = start.toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
            }

            return {
                id: event.id, // Use Google's unique ID
                header: event.summary, // Map Google 'summary' to 'header'
                message: cleanGoogleDescription(event.description) || "No description available.", // Map 'description' to 'message', handle null
                time_to_send: displayTime, // Use the formatted start time
                tags: ["Upcoming events"],
                source: "GOOGLE_CALENDAR" // Internal flag for filtering/display
            };
        });

        return transformedEvents;

    } catch (error) {
        console.error("Error fetching Google Calendar events:", error);
        // Log more details if available
        if (error.response) {
            console.error("Google API Error Response:", error.response.data);
        }
        return []; // Return empty array on error
    }
}